// The Grail API's GrailFind calls (/api/find, api/lib/grail_web/controllers/find_controller.ex). A signed-in buyer's
// session token is kept in this browser and sent with every call; a 401 means it ended, so the site forgets it.

// The dev API on this computer only in `vite` and the tests: a built site has its address or calls nowhere.
export const API_URL = ((import.meta.env.VITE_API_URL as string | undefined) ?? (import.meta.env.DEV ? 'http://localhost:4000' : '')).replace(/\/$/, '');

const TOKEN = 'grailfind-token';
const SIGNED_OUT = 'grailfind:signed-out';

export function token(): string | null {
  try {
    return localStorage.getItem(TOKEN);
  } catch {
    return null;
  }
}

export function setToken(value: string | null) {
  try {
    if (value) localStorage.setItem(TOKEN, value);
    else localStorage.removeItem(TOKEN);
  } catch {
    // A browser that keeps nothing: the buyer stays signed in for this page only.
  }
  window.dispatchEvent(new Event(SIGNED_OUT));
}

/** Calls `onChange` when the buyer signs in or out, in this tab or another. */
export function onSessionChange(onChange: () => void) {
  const storage = (e: StorageEvent) => {
    if (e.key === TOKEN) onChange();
  };
  window.addEventListener(SIGNED_OUT, onChange);
  window.addEventListener('storage', storage);
  return () => {
    window.removeEventListener(SIGNED_OUT, onChange);
    window.removeEventListener('storage', storage);
  };
}

/** A call that did not work: `status` 0 is no answer at all (no signal at the show). */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
  ) {
    super(code);
  }
}

export async function call<T>(path: string, init: { method?: string; body?: unknown; cache?: RequestCache } = {}): Promise<T> {
  const headers: Record<string, string> = { accept: 'application/json' };
  const t = token();
  if (t) headers.authorization = `Bearer ${t}`;
  if (init.body !== undefined) headers['content-type'] = 'application/json';

  let res: Response;
  try {
    res = await fetch(API_URL + path, {
      method: init.method ?? 'GET',
      headers,
      cache: init.cache,
      body: init.body === undefined ? undefined : JSON.stringify(init.body),
    });
  } catch {
    throw new ApiError(0, 'offline');
  }

  const data = (await res.json().catch(() => ({}))) as { error?: string };
  if (res.status === 401 && t) setToken(null);
  if (!res.ok) throw new ApiError(res.status, data.error ?? 'failed');
  return data as T;
}

/** A path on the API (a floor plan picture) as a full address. */
export const apiUrl = (path: string) => API_URL + path;

// What the API sends (api/lib/grail_web/find_json.ex).
export type Slab = { grader: string; grade: string };

export type ShowRow = { slug: string; name: string; when: string; hours: string | null; shops: number; city: string | null };

export type ShowsAnswer = { shows: ShowRow[]; cities: string[]; filters: { city: string | null; date: string | null } };

export type Table = { id: string; label: string; across: number; down: number; shops: string[] };

export type Vendor = { shop: string; table_label: string | null; cards: number; on_plan: boolean };

export type Availability = 'available' | 'held' | 'sold';

export type CatalogueCard = {
  availability?: Availability;
  id: string;
  name: string;
  edition: string | null;
  grade: string | null;
  set: string | null;
  price: string | null;
  picture: string | null;
  slab: Slab | null;
  shop: string;
  table_label: string | null;
  on_plan: boolean;
};

export type Filters = {
  q: string | null;
  slabs: boolean;
  grader: string | null;
  grade: string | null;
  lang: string | null;
  set: string | null;
  min: string | null;
  max: string | null;
};

export type ShowAnswer = {
  checked_at?: string;
  show: { slug: string; name: string; when: string; hours: string | null };
  plan: { url: string } | null;
  tables: Table[];
  lit: string | null;
  vendors: Vendor[];
  sets: string[];
  graders: string[];
  languages: { code: string; name: string }[];
  filters: Filters;
  filtered: boolean;
  cards: CatalogueCard[];
  total: number;
  page: number;
  pages: number;
};

export type Hold = {
  id: string;
  state: 'asked' | 'held' | 'expired' | 'declined' | 'cancelled' | 'released';
  open: boolean;
  minutes: number;
  held_until: string | null;
  held_until_clock: string | null;
};

export type Item = {
  availability?: Availability;
  checked_at?: string;
  id: string;
  name: string;
  set: string;
  grade: string | null;
  price: string | null;
  cents: number | null;
  currency: string;
  symbol: string;
  picture: string | null;
  shop: string;
  show: string | null;
  grader: string | null;
  grade_value: string | null;
  kind: 'raw' | 'graded' | 'sealed';
  grader_name: string | null;
  cert: string | null;
  back: { slug: string; name: string } | null;
  plan: { slug: string; table: string } | null;
  account: { on_list: boolean; count: number; hold: Hold | null } | null;
};

export type ListCard = {
  id: string;
  name: string;
  grade: string | null;
  price: string | null;
  cents: number | null;
  currency: string;
  symbol: string;
  picture: string | null;
  slab: Slab | null;
  shop: string;
  for_sale: boolean;
};

export type HoldRow = Hold & { card: { id: string; name: string; shop: string } };

export type ListAnswer = { cards: ListCard[]; holds: HoldRow[]; max: number };

export type Me = { user: { name: string | null; email: string; site_admin: boolean }; count: number };
